import BaseApiClient from '../BaseApiClient';
import { filenameFromContentDisposition } from '../savings-products/response-helpers';
import {
  SAVINGS_API_VERSION_HEADERS,
  toSavingsListResponse
} from '../savings-reference-resource';
import type { ListDataResponse } from '../types';
import type {
  SavingsStatement,
  SavingsStatementListParams,
  SavingsStatementPdfParams
} from '@/interfaces/SavingsStatement.interface';

/**
 * Internal monthly Savings statements (FLT-2081). `statement_month` is a
 * top-level parameter, not a `filter[...]`, so the shared `getList` helper
 * is not used.
 */
export class ApiSavingsStatements extends BaseApiClient {
  private readonly path = '/savings-statements';

  async list(
    params: SavingsStatementListParams
  ): Promise<ListDataResponse<SavingsStatement>> {
    const query = new URLSearchParams({
      statement_month: params.statement_month,
      'page[number]': String(params.page),
      'page[size]': String(params.perPage)
    });
    const search = params.search?.trim();
    if (search) query.set('filter[search]', search);

    const res = await this.client.get(`${this.path}?${query.toString()}`, {
      headers: SAVINGS_API_VERSION_HEADERS
    });
    return toSavingsListResponse<SavingsStatement>(res.data);
  }

  async getPdf(
    params: SavingsStatementPdfParams
  ): Promise<{ blob: Blob; filename: string }> {
    const query = new URLSearchParams({
      savings_customer_id: String(params.savings_customer_id),
      statement_month: params.statement_month,
      currency: params.currency
    });
    const res = await this.client.get(`${this.path}/pdf?${query.toString()}`, {
      responseType: 'blob',
      headers: { ...SAVINGS_API_VERSION_HEADERS, Accept: 'application/pdf' }
    });
    return {
      blob: new Blob([res.data], { type: 'application/pdf' }),
      filename: filenameFromContentDisposition(
        res.headers['content-disposition'],
        `savings-statement-${params.savings_customer_id}_${params.currency}_${params.statement_month}.pdf`
      )
    };
  }
}

export * from './use-savings-statements-mutations';
